import React, { forwardRef, useEffect, useReducer, useState } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import IBuildrProps from '../../../utilities/IBuildrProps';
import { Button } from 'react-bootstrap';
import TextArea from "../../Form/TextArea";
import { valuesReducer } from "../../../reducers/valuesReducer";
import { REDUCERS } from '../../../reducers/reducerConstants';
import FormCheckbox from '../../Form/FormCheckbox';
import EmailInput from "../../Emailer/EmailInput";
import { validate, validateArrayObjects } from "../../../utilities/formValidators";
import { INPUT_TYPE } from '../../../utilities/validatorConstants';
import { sendFeedback } from "../services";
import ReCaptcha from '../../ReCaptcha';

const feedbackFormValues = {
    FeedbackText: { value: "", isValid: false, required: true },
    Name: { value: "", isValid: false, required: false },
    Email: { value: "", isValid: false, required: false },
    ReCaptchaResponse: { value: "", isValid: false, required: true }
};

interface IValue {
    value: string;
    isValid: boolean;
    required: boolean;
    name?: string;
}

interface IFormValues {
    FeedbackText: IValue;
    Name: IValue;
    Email: IValue;
    ReCaptchaResponse: IValue;
}

interface IFeedbackEmailerProps {
    onChange: (name: string, value: string, valid: boolean) => void;
    name: string;
    email: string;
}

const Emailer = ({ onChange, name, email }: IFeedbackEmailerProps) => {
    const inputProps = {
        inputClassName: "bg-gray-200 border-0",
        onChange,
        show: true,
        errorClassName: "text-dark"
    };

    return (
        <div className="row">
            <EmailInput {...inputProps} className="col-12 col-md-6 pl-0 pr-3" label="Name*" id="Name" inputType="text" value={name} />
            <EmailInput {...inputProps} className="col-12 col-md-6 px-0" label="Email Address*" id="Email" inputType="email" value={email} />
        </div>
    );
}

const FeedbackContainer = styled.div<{ ref: any }>`
  .feedback-header {
      font-size: 18px;
  }

  .feedback-textarea-container {
      textarea {
        height: 200px;
        background-color: #eff0f1;
        resize: none;
        border-radius: 2px;
        outline: none;
        font-size: 18px;
      }
  }
`

interface IFeedbackFormProps {
    disable: boolean;
    onChange: (name: string, value: string, isValid: boolean, required: boolean) => void;
    values: IFormValues;
    onSubmit: (e) => void;
    onCheckboxChange: (e) => void;
    contactMe: boolean;
}

const FeedbackForm = ({ disable, onChange, values, onSubmit, onCheckboxChange, contactMe }: IFeedbackFormProps) => {
    function handleTextAreaChange(e) {
        const { name, value } = e.target;

        if (validate(value, INPUT_TYPE.TEXT)) {
            onChange(name, value, true, true);
        } else {
            onChange(name, value, true, false);
        }
    }

    function handleEmailerChange(name: string, value: string, valid: boolean) {
        onChange(name, value, true, valid)
    }

    function handleReCaptchaChange(value: string) {
        onChange("ReCaptchaResponse", value, true, true);
    }

    return (
        <>
            <div className="row">
                <h3 className="text-sans text-primary">How can we improve this answer?</h3>
                <div className="col-12 p-0">
                    <TextArea textContainerClassName="my-0" textAreaClassName="pl-2 pt-2 border-0 w-100 mb-0" className="feedback-textarea-container mb-0" id="FeedbackText" placeholder="Please send us your feedback on how we can improve this answer." onChange={handleTextAreaChange} required={values.FeedbackText.required} value={values.FeedbackText.value} />
                </div>
            </div>
            <div className="my-5">
                <FormCheckbox id="contact-me" inputClassName="contact-me-checkbox" value={contactMe} onChange={onCheckboxChange} label="Please contact me regarding this feedback" />
                {contactMe && <Emailer onChange={handleEmailerChange} name={values.Name.value} email={values.Email.value} />}
            </div>
            <ReCaptcha onChange={handleReCaptchaChange} />
            <Button className="px-6 mt-4" onClick={onSubmit} disabled={disable}>Submit</Button>
        </>
    );
}

const Feedback = forwardRef((props: IBuildrProps, ref) => {
    const [values, valuesDispatch] = useReducer(valuesReducer, { ...feedbackFormValues });
    const [wasClicked, setWasClicked] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [contactMe, setContactMe] = useState(false);
    const [disabled, setDisabled] = useState(true);
    const [error, setError] = useState(null);

    //when a value changes validate and toggle the submit button
    useEffect(() => {
        const isValid = validateArrayObjects(values);
        setDisabled(!isValid);
    }, [values]);

    //reset the email forms on open/close of contactMe
    useEffect(() => {
        handleFeedbackFormChange("Email", "", contactMe, false);
        handleFeedbackFormChange("Name", "", contactMe, false);
    }, [contactMe]);

    //handle the Yes No "was this helpful" button clicks
    function handleClick(helpful: boolean) {
        setWasClicked(true);
        setShowForm(!helpful);
    }

    function handleFeedbackFormChange(name, value, required, isValid) {
        valuesDispatch({ type: REDUCERS.SET_VALUE, name, value, required, isValid });
    }

    async function handleSubmit(e) {
        try {
            setError(null);
            const details = createFeedbackObject(values);
            const feedback = await sendFeedback(details);

            if (feedback) {
                setShowForm(false);
            } else {
                throw "There was an error sending your feedback. Please try again later";
            }
        } catch (err) {
            setError("There was an error sending your feedback. Please try again later");
        }
    }

    function createFeedbackObject(values) {
        return {
            message: values.FeedbackText.value,
            email: values.Email.value,
            path: window.location.pathname,
            name: values.Name.value,
            reCaptchaResponse: values.ReCaptchaResponse.value
        }
    }

    function handleCheckboxChange(e) {
        setContactMe(e.target.checked);
    }

    return (
        <FeedbackContainer data-id={props.cmsId} ref={ref} className={classNames(props.editorClassObj, "my-6")}>
            {(!showForm && !wasClicked) &&
                <div>
                    <p className="feedback-header">Was this answer helpful</p>
                    <div className="mt-3">
                        <Button id="helpfulFeedbackYes" variant="primary" className="py-2" onClick={() => handleClick(true)}>Yes</Button>
                        <Button id="helpfulFeedbackNo" className="ml-1 py-2" variant="outline-primary" onClick={() => handleClick(false)}>No</Button>
                    </div>
                </div>
            }
            {(wasClicked && showForm) &&
                <FeedbackForm disable={disabled} contactMe={contactMe} onCheckboxChange={handleCheckboxChange} onSubmit={handleSubmit} onChange={handleFeedbackFormChange} values={values} />
            }
            {(wasClicked && !showForm) &&
                <p className="feedback-header">Thank you for your feedback!</p>
            }
            {error &&
                <p className="text-primary">{error}</p>
            }
        </FeedbackContainer>
    )
});

export default Feedback;